import React, { useState, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import { AuthContext } from '../context/AuthContext';
import { submitSupportForm } from '../utils/api';
import { FaArrowLeft, FaPaperclip, FaSpinner } from 'react-icons/fa';
import './ContactITSupport.css';

const MODULE_OPTIONS = [
  'Dashboard',
  'Organization Structure',
  'Workflows',
  'Company OKRs',
  'Personal OKRs',
  'Team OKRs',
  'My OKRs',
  'Annual OKRs',
  'People',
  'Teams',
  'Departments',
  'Visual Map',
  'Safety Procedures',
  'SLAs',
  'Chat',
  'Cost Cards',
  'Template Picker',
  'Create Cost Card',
  'Edit Cost Card',
  'Create Custom Template',
  'Edit Custom Template',
  'Create OKR',
  'Edit OKR',
  'Technical Sales Workflow',
  'Store Management Workflow',
  'Sales Workflow',
  'Import Workflow',
  'Job Descriptions',
  'Employees Reviews',
  'Interview Processes',
  'Company Strategy',
  'Business Continuity Plan',
  'Data Management SOP',
  'Procurement SOP',
  'Risk Compliance SOP',
  'Settings',
  'Search Results',
  'Other',
];

const PRIORITY_OPTIONS = [
  'Critical',
  'High',
  'Medium',
  'Low',
];

function getBrowserInfo() {
  const ua = navigator.userAgent;
  let browser = 'Unknown';
  if (ua.indexOf('Chrome') > -1) browser = 'Chrome';
  else if (ua.indexOf('Safari') > -1) browser = 'Safari';
  else if (ua.indexOf('Firefox') > -1) browser = 'Firefox';
  else if (ua.indexOf('MSIE') > -1 || ua.indexOf('Trident/') > -1) browser = 'IE';
  return browser;
}

function getOSInfo() {
  const platform = navigator.platform;
  if (/Win/.test(platform)) return 'Windows';
  if (/Mac/.test(platform)) return 'MacOS';
  if (/Linux/.test(platform)) return 'Linux';
  if (/Android/.test(platform)) return 'Android';
  if (/iPhone|iPad|iPod/.test(platform)) return 'iOS';
  return 'Unknown';
}

export default function ContactITSupport() {
  const navigate = useNavigate();
  const { user } = useContext(AuthContext);
  const [name, setName] = useState(user?.name || '');
  const [email, setEmail] = useState(user?.email || '');
  const [issueTitle, setIssueTitle] = useState('');
  const [module, setModule] = useState('Dashboard');
  const [description, setDescription] = useState('');
  const [errorMessage, setErrorMessage] = useState('');
  const [expected, setExpected] = useState('');
  const [steps, setSteps] = useState(['']);
  const [priority, setPriority] = useState('Medium');
  const [attachment, setAttachment] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [submitted, setSubmitted] = useState(false);
  const [ticketId, setTicketId] = useState('');

  // Function to check if a field is valid
  const isFieldValid = (fieldName, value) => {
    switch (fieldName) {
      case 'name':
        return value.trim().length > 0;
      case 'email':
        return value.trim().length > 0 && value.includes('@');
      case 'issueTitle':
        return value.trim().length > 0;
      case 'description':
        return value.trim().length > 0;
      case 'expected':
        return value.trim().length > 0;
      case 'steps':
        return steps.filter(s => s.trim()).length > 0;
      case 'attachment':
        return attachment !== null;
      default:
        return true;
    }
  };

  const handleFileChange = e => {
    setAttachment(e.target.files[0]);
  };

  const handleStepChange = (idx, value) => {
    const newSteps = [...steps];
    newSteps[idx] = value;
    setSteps(newSteps);
  };

  const addStep = () => setSteps([...steps, '']);
  const removeStep = idx => setSteps(steps.filter((_, i) => i !== idx));

  const handleSubmit = async e => {
    e.preventDefault();
    setError('');
    
    // Comprehensive validation
    if (!name.trim()) {
      setError('Name is required.');
      return;
    }
    
    if (!email.trim()) {
      setError('Email is required.');
      return;
    }
    
    if (!issueTitle.trim()) {
      setError('Issue title is required.');
      return;
    }
    
    if (!description.trim()) {
      setError('Detailed description is required.');
      return;
    }
    
    if (!expected.trim()) {
      setError('Please describe what happened vs. what you expected.');
      return;
    }
    
    // Validate that at least one step is filled out
    const validSteps = steps.filter(s => s.trim());
    if (validSteps.length === 0) {
      setError('Please provide at least one step to reproduce the issue.');
      return;
    }
    
    // Validate that a file is attached
    if (!attachment) {
      setError('Please attach a file (image, log, text, or PDF).');
      return;
    }
    
    setSubmitting(true);
    const timestamp = new Date().toISOString();
    const browser = getBrowserInfo();
    const os = getOSInfo();
    const device = navigator.userAgent;
    
    try {
      const result = await submitSupportForm({
        name,
        email,
        issueTitle,
        module,
        description,
        errorMessage,
        expected,
        steps: validSteps,
        priority,
        attachment,
        timestamp,
        browser,
        os,
        device,
      });
      
      if (result && result.success) {
        setSubmitted(true);
        setTicketId(result.ticketId || 'N/A');
      } else {
        setError('Failed to submit. Please try again.');
      }
    } catch (err) {
      setError('Failed to submit. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  if (submitted) {
    return (
      <div className="contact-it-support-page">
        <div className="contact-it-support-header">
          <button 
            className="back-button"
            onClick={() => navigate('/it-support')}
          >
            <FaArrowLeft /> Back to IT Support
          </button>
          <h1>Contact IT Support</h1>
        </div>
        
        <div className="success-message">
          <div className="success-icon">✅</div>
          <h2>Thank you! Your ticket has been submitted.</h2>
          <div className="ticket-info">
            <strong>Your ticket:</strong> <span className="ticket-id">#{ticketId}</span>
          </div>
          <p>We'll get back to you in <strong>1-2 business days</strong>.</p>
          <div className="success-actions">
            <button 
              className="primary-button"
              onClick={() => navigate('/it-support')}
            >
              View My Tickets
            </button>
            <button 
              className="secondary-button"
              onClick={() => {
                setSubmitted(false);
                setIssueTitle('');
                setDescription('');
                setErrorMessage('');
                setExpected('');
                setSteps(['']);
                setPriority('Medium');
                setAttachment(null);
                setError('');
              }}
            >
              Submit Another Ticket
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="contact-it-support-page">
      <div className="contact-it-support-header">
        <button 
          className="back-button"
          onClick={() => navigate('/it-support')}
        >
          <FaArrowLeft /> Back to IT Support
        </button>
        <h1>Contact IT Support</h1>
        <p className="header-description">
          Please provide detailed information about your issue so we can assist you effectively.
        </p>
      </div>

      <div className="contact-it-support-content">
        <div className="form-note">
          <p><strong>Note:</strong> Fields marked with * are required. Please provide as much detail as possible to help us assist you effectively.</p>
        </div>
        <div className="form-help">
          <p><strong>Tips for a better ticket:</strong></p>
          <ul>
            <li>Be specific about when and where the issue occurs</li>
            <li>Include any error messages you see</li>
            <li>Describe what you were trying to do</li>
            <li>List the exact steps to reproduce the problem</li>
            <li>Attach relevant files (screenshots, error logs, etc.) to help us understand the issue better</li>
          </ul>
        </div>
        <form className="support-form" onSubmit={handleSubmit}>
          <div className="form-section">
            <h3>Contact Information</h3>
            <div className="form-row">
              <div className="form-group">
                <label htmlFor="name">Name *</label>
                <input
                  id="name"
                  type="text"
                  value={name}
                  onChange={e => setName(e.target.value)}
                  required
                  placeholder="Your full name"
                />
              </div>
              <div className="form-group">
                <label htmlFor="email">Email *</label>
                <input
                  id="email"
                  type="email"
                  value={email}
                  onChange={e => setEmail(e.target.value)}
                  required
                  placeholder="your.email@company.com"
                />
              </div>
            </div>
          </div>

          <div className="form-section">
            <h3>Issue Details</h3>
            <div className="form-group">
              <label htmlFor="issueTitle">Issue Title *</label>
              <input
                id="issueTitle"
                type="text"
                value={issueTitle}
                onChange={e => setIssueTitle(e.target.value)}
                required
                maxLength={100}
                placeholder="Brief description of the issue"
              />
            </div>

            <div className="form-row">
              <div className="form-group">
                <label htmlFor="module">Module / Page *</label>
                <select
                  id="module"
                  value={module}
                  onChange={e => setModule(e.target.value)}
                  required
                >
                  {MODULE_OPTIONS.map(opt => (
                    <option key={opt} value={opt}>{opt}</option>
                  ))}
                </select>
              </div>
              <div className="form-group">
                <label htmlFor="priority">Priority / Severity *</label>
                <select
                  id="priority"
                  value={priority}
                  onChange={e => setPriority(e.target.value)}
                  required
                >
                  {PRIORITY_OPTIONS.map(opt => (
                    <option key={opt} value={opt}>{opt}</option>
                  ))}
                </select>
              </div>
            </div>

            <div className="form-group">
              <label htmlFor="description">Detailed Description *</label>
              <textarea
                id="description"
                value={description}
                onChange={e => setDescription(e.target.value)}
                rows={4}
                required
                placeholder="Describe the issue in detail..."
              />
            </div>

            <div className="form-group">
              <label htmlFor="errorMessage">Error Message (if any)</label>
              <textarea
                id="errorMessage"
                value={errorMessage}
                onChange={e => setErrorMessage(e.target.value)}
                rows={2}
                placeholder="Please include any error messages you received..."
              />
            </div>

            <div className="form-group">
              <label htmlFor="expected">What happened vs. what you expected *</label>
              <textarea
                id="expected"
                value={expected}
                onChange={e => setExpected(e.target.value)}
                rows={2}
                required
                placeholder="What did you expect to happen?"
              />
            </div>
          </div>

          <div className="form-section">
            <h3>Steps to Reproduce *</h3>
            <div className="steps-container">
              {steps.map((step, idx) => (
                <div key={idx} className="step-input">
                  <span className="step-number">{idx + 1}.</span>
                  <input
                    type="text"
                    value={step}
                    onChange={e => handleStepChange(idx, e.target.value)}
                    placeholder={`Step ${idx + 1}`}
                    required
                  />
                  {steps.length > 1 && (
                    <button
                      type="button"
                      onClick={() => removeStep(idx)}
                      className="remove-step-btn"
                    >
                      ×
                    </button>
                  )}
                </div>
              ))}
              <button
                type="button"
                onClick={addStep}
                className="add-step-btn"
              >
                + Add Step
              </button>
            </div>
          </div>

          <div className="form-section">
            <h3>Attachment *</h3>
            <div className="form-group">
              <label htmlFor="attachment">File Attachment *</label>
              <input
                id="attachment"
                type="file"
                accept="image/*,.log,.txt,.pdf"
                onChange={handleFileChange}
                className="file-input"
                required
              />
              <small><strong>Required:</strong> Please attach a relevant file (screenshots, error logs, etc.) to help us understand your issue. Accepted formats: Images, Log files, Text files, PDFs. Maximum file size: 10MB.</small>
            </div>
          </div>

          {error && (
            <div className="error-message">
              {error}
            </div>
          )}

          <div className="form-actions">
            <button
              type="button"
              className="cancel-button"
              onClick={() => navigate('/it-support')}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="submit-button"
              disabled={submitting}
            >
              {submitting ? (
                <>
                  <FaSpinner className="spin" />
                  Submitting...
                </>
              ) : (
                'Submit Ticket'
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
